import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { currentWeek } from "@/lib/weeks";
import { TimesheetFilters } from "@/components/timesheet/TimesheetFilters";
import { OperatorSheet } from "@/components/timesheet/OperatorSheet";
import { DayEditorDialog } from "@/components/timesheet/DayEditorDialog";

const now = new Date();
const cw = currentWeek();

export default function Timesheet() {
  const { user, pharmacy } = useAuth();
  const isOwner = user?.role === "owner";
  const [f, setF] = useState({ view: "week", year: cw.year, week: cw.week, month: now.getMonth() + 1, operatorId: "" });
  const [operators, setOperators] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const params = useCallback(() => ({
    year: f.year, view: f.view,
    ...(f.view === "week" ? { week: f.week } : { month: f.month }),
    ...(f.operatorId ? { operator_id: f.operatorId } : {}),
  }), [f]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/timesheet", { params: params() });
      setData(d);
      setTarget((t) => {
        if (!t) return t;
        const op = d.operators.find((o) => o.operator_id === t.op.operator_id);
        const day = op?.days.find((x) => x.date === t.day.date);
        return op && day ? { op, day } : null;
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isOwner) return;
    api.get("/operators").then((r) => setOperators(r.data.filter((x) => x.role === "operator"))).catch((e) => toast.error(apiError(e)));
  }, [isOwner]);

  const download = async (format) => {
    try {
      const res = await api.get(`/export/timesheet-${format}`, { params: params(), responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `cartellino_${f.year}_${f.view === "week" ? `sett${String(f.week).padStart(2, "0")}` : `mese${String(f.month).padStart(2, "0")}`}.${format}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Cartellino ${format.toUpperCase()} scaricato`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      <div>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">{isOwner ? "Report e Cartellini" : "Il mio cartellino"}</h1>
        <p className="text-sm text-gray-600 mt-1">
          Vista settimanale o mensile con ore contrattuali, lavorate, straordinari, pause, causali di assenza e note.
          <span className="ml-2 inline-flex items-center gap-1"><i className="inline-block h-3 w-3 rounded bg-orange-200" /> festivo</span>
          <span className="ml-2 inline-flex items-center gap-1"><i className="inline-block h-3 w-3 rounded bg-red-100 border border-red-200" /> domenica</span>
          <span className="ml-2 inline-flex items-center gap-1"><i className="inline-block h-3 w-3 rounded bg-emerald-100 border border-emerald-600" /> totale settimana</span>
        </p>
      </div>

      <TimesheetFilters f={f} set={set} operators={isOwner ? operators : null} canExport={isOwner} onExport={download} />

      {data && <p data-testid="ts-period-label" className="text-sm font-semibold text-gray-700">{data.period.label}{loading ? " · aggiornamento…" : ""}</p>}

      {!data && loading && <p className="text-sm text-gray-500">Caricamento...</p>}
      {data && data.operators.length === 0 && <p data-testid="ts-empty" className="text-sm text-gray-600 bg-white border border-gray-200 rounded-xl p-6">Nessun operatore da mostrare.</p>}
      {data?.operators.map((op) => <OperatorSheet key={op.operator_id} op={op} canEdit={isOwner} onEditDay={(o, d) => setTarget({ op: o, day: d })} />)}

      {isOwner && <DayEditorDialog target={target} types={pharmacy?.absence_types || []} onClose={() => setTarget(null)} onChanged={load} />}
    </div>
  );
}
